// src/utils/timeRangeUtils.js

/**
 * Get all available time range options
 * @returns {Array} Array of time range options with value and label
 */
export const getTimeRangeOptions = () => {
    return [
        // Recent periods
        { value: 'last_1h', label: 'Last Hour' },
        { value: 'today', label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: 'last_24h', label: 'Last 24 Hours' },
        
        // Weekly periods
        { value: 'this_week', label: 'This Week' },
        { value: 'last_week', label: 'Last Week' },
        { value: 'last_7_days', label: 'Last 7 Days' },
        
        // Monthly periods
        { value: 'this_month', label: 'This Month' },
        { value: 'last_month', label: 'Last Month' },
        { value: 'last_30_days', label: 'Last 30 Days' },
        { value: 'last_90_days', label: 'Last 90 Days' },
        
        // Extended periods
        { value: 'last_3_months', label: 'Last 3 Months' },
        { value: 'last_6_months', label: 'Last 6 Months' },
        { value: 'last_12_months', label: 'Last 12 Months' },
        
        // Quarterly and yearly
        { value: 'current_quarter', label: 'Current Quarter' },
        { value: 'last_quarter', label: 'Last Quarter' },
        { value: 'this_year', label: 'This Year' },
        { value: 'last_year', label: 'Last Year' },
        { value: 'year_to_date', label: 'Year to Date' },
        { value: 'last_365_days', label: 'Last 365 Days' },
        
        // Long-term periods
        { value: '5_years', label: 'Last 5 Years' },
        { value: '10_years', label: 'Last 10 Years' },
        { value: 'all_time', label: 'All Time' },
        
        // Custom range
        { value: 'custom', label: 'Custom Range' }
    ];
};

/**
 * Get the number of days in a time range
 * @param {string} range Time range preset
 * @returns {number} Number of days in the time range
 */
export const getTimeRangeDays = (range) => {
    switch(range) {
        // Recent periods
        case 'last_1h': return 1/24; // Fraction of a day
        case 'today': return 1;
        case 'yesterday': return 1;
        case 'last_24h': return 1;
        
        // Weekly periods
        case 'this_week': return 7;
        case 'last_week': return 7;
        case 'last_7_days': return 7;
        
        // Monthly periods
        case 'this_month': 
            const now = new Date();
            return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        case 'last_month':
            const lastMonth = new Date();
            lastMonth.setMonth(lastMonth.getMonth() - 1);
            return new Date(lastMonth.getFullYear(), lastMonth.getMonth() + 1, 0).getDate();
        case 'last_30_days': return 30;
        case 'last_90_days': return 90;
        
        // Extended periods
        case 'last_3_months': return 90;
        case 'last_6_months': return 180;
        case 'last_12_months': return 365;
        
        // Quarterly and yearly
        case 'current_quarter': return 90;
        case 'last_quarter': return 90;
        case 'this_year':
            const thisYear = new Date();
            const startOfYear = new Date(thisYear.getFullYear(), 0, 1);
            return Math.floor((thisYear - startOfYear) / (24 * 60 * 60 * 1000)) + 1;
        case 'last_year': return 365;
        case 'year_to_date':
            const ytdNow = new Date();
            const ytdStartOfYear = new Date(ytdNow.getFullYear(), 0, 1);
            return Math.floor((ytdNow - ytdStartOfYear) / (24 * 60 * 60 * 1000)) + 1;
        case 'last_365_days': return 365;
        
        // Long-term periods
        case '5_years': return 365 * 5;
        case '10_years': return 365 * 10;
        case 'all_time': return 365 * 10; // Default to 10 years for "all time"
        
        default: return 30;
    }
};

/**
 * Build a time range object for API consumption
 * @param {string} preset Time range preset
 * @param {string} startDate Optional start date (for custom range)
 * @param {string} endDate Optional end date (for custom range)
 * @returns {Object} Formatted time range object
 */
export const buildTimeRange = (preset, startDate = null, endDate = null) => {
    if (preset === 'custom' && startDate && endDate) {
        return {
            type: 'custom',
            start_date: startDate,
            end_date: endDate
        };
    }

    return {
        type: 'preset',
        preset: preset
    };
};

/**
 * Format a time range for API consumption
 * @param {string} preset Time range preset
 * @param {string} startDate Start date (for custom range)
 * @param {string} endDate End date (for custom range)
 * @param {boolean} isCustomRange Whether it's a custom range
 * @returns {Object} Formatted time range object
 */
export const formatTimeRange = (preset, startDate = null, endDate = null, isCustomRange = false) => {
    // If using custom dates, format using buildTimeRange
    if (isCustomRange && startDate && endDate) {
        return buildTimeRange('custom', startDate, endDate);
    }

    // If using preset, create formatted object using buildTimeRange
    return buildTimeRange(preset);
};

/**
 * Get previous period based on current period selection
 * @param {string} currentPreset Current time range preset
 * @param {string} startDate Start date (for custom range)
 * @param {string} endDate End date (for custom range)
 * @param {boolean} isCustomRange Whether the current range is a custom range
 * @returns {Object} Previous period time range object
 */
export const getPreviousPeriod = (currentPreset, startDate = null, endDate = null, isCustomRange = false) => {
    if (currentPreset === 'this_month' || currentPreset === 'last_30_days') {
        // Previous 30 days before current period
        const prevStart = new Date();
        prevStart.setDate(prevStart.getDate() - 60);
        const prevEnd = new Date();
        prevEnd.setDate(prevEnd.getDate() - 30);

        return buildTimeRange('custom',
            prevStart.toISOString(),
            prevEnd.toISOString()
        );
    } else if (currentPreset === 'last_3_months') {
        // Previous 3 months before current period
        const prevStart = new Date();
        prevStart.setMonth(prevStart.getMonth() - 6);
        const prevEnd = new Date();
        prevEnd.setMonth(prevEnd.getMonth() - 3);

        return buildTimeRange('custom',
            prevStart.toISOString(),
            prevEnd.toISOString()
        );
    } else if (currentPreset === 'year_to_date') {
        // Same period last year
        const lastYear = new Date().getFullYear() - 1;
        return buildTimeRange('custom',
            new Date(lastYear, 0, 1).toISOString(),
            new Date(lastYear, new Date().getMonth(), new Date().getDate()).toISOString()
        );
    } else if (isCustomRange && startDate && endDate) {
        // For custom range, use same length period before
        const startDt = new Date(startDate);
        const endDt = new Date(endDate);
        const diffTime = Math.abs(endDt - startDt);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        const prevStart = new Date(startDt);
        prevStart.setDate(prevStart.getDate() - diffDays);
        const prevEnd = new Date(startDt);
        prevEnd.setDate(prevEnd.getDate() - 1);

        return buildTimeRange('custom',
            prevStart.toISOString(),
            prevEnd.toISOString()
        );
    } else {
        // For other cases, generate equivalent previous period
        const days = getTimeRangeDays(currentPreset);
        return buildTimeRange('custom',
            new Date(Date.now() - 2 * (days * 24 * 60 * 60 * 1000)).toISOString(),
            new Date(Date.now() - (days * 24 * 60 * 60 * 1000)).toISOString()
        );
    }
};

/**
 * Handle time range preset change
 * @param {Event|string} selectedValue New time range preset value or event
 * @param {Function} setTimeRangePreset State setter for timeRangePreset
 * @param {Function} setShowCustomRange State setter for showCustomRange
 */
export const handleTimeRangeChange = (selectedValue, setTimeRangePreset, setShowCustomRange) => {
    // Check if we're dealing with an event or direct value from the component
    const value = selectedValue?.target?.value || selectedValue;
    setTimeRangePreset(value);
    setShowCustomRange(value === 'custom');
};

/**
 * Calculate percentage change between current and previous periods
 * @param {number} current Current value
 * @param {number} previous Previous value
 * @returns {number} Percentage change
 */
export const calculatePeriodComparison = (current, previous) => {
    if (!current || !previous || previous === 0) return 0;
    return ((current - previous) / Math.abs(previous)) * 100;
};
